import { fromOthersNodeName } from "motw_core/Repositories/WorkflowRepository";
import * as React from "react";
import {
    GetBookOfMindPageAudioDiagnostics,
    GetBookOfMindStateForPageAudio,
} from "source/app/presentation/MainPanel/Workflows/BookOfMind/Helpers/BookOfMindQuoteDisplay";
import { IBookOfMindState } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Helpers/IBookOfMindState";
import { bookOfMindSpeech } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Speech/BookOfMindSpeech";
import type { IBookOfMindPageAudioDiagnostics } from "source/app/services/External/LogicServer/IBookOfMindPageAudioDiagnostics";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import Glyphicon from "source/app/presentation/Shared/Glyphicon/Glyphicon";
import IconWithText from "source/app/services/Shared/IconWithText/IconWithText";
import * as toastr from "toastr";
import { ActiveSpeechControls } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Tools/Speak/Active/ActiveSpeechControls";
import Render from "source/app/presentation/Shared/Render";
import {
    BookOfMindPlaybackMode,
    PlaybackModePicker,
} from "./PlaybackModePicker";
import { SpeechControlsCrossFade } from "./SpeechControlsCrossFade";
import { ToggleSpeech } from "./ToggleSpeech";
import * as styles from "./SpeechControls.css";
import { TouchActionButton } from "source/app/presentation/Shared/TouchActionButton/TouchActionButton";
import { Sounds } from "source/app/services/Sound/Sounds";
import { LogClientDiagnostic } from "source/app/services/ClientDiagnostics";
import {
    GetBookOfMindSavedVoiceName,
    IsGoogleBookOfMindVoice,
} from "../../Speech/Helpers/BookOfMindVoice";

export function SpeakQuotesLink({ state }: { state: IBookOfMindState }) {
    const [pageAudioSnapshot, setPageAudioSnapshot] = React.useState(
        bookOfMindSpeech.GetPageAudioSnapshot()
    );

    React.useEffect(() => {
        const unsubscribe = bookOfMindSpeech.SubscribePageAudio(
            setPageAudioSnapshot
        );
        return () => {
            unsubscribe();
        };
    }, []);

    const isPlayingOrPaused =
        state.isSpeechActive || state.spokenQuoteIndex !== undefined;
    const speechControlsKey = isPlayingOrPaused
        ? `active_${state.isSpeechActive}_${state.spokenQuoteIndex ?? "starting"}_${state.itemsToSpeak.length}`
        : `start_${state.isSpeechStarting ? "starting" : "ready"}`;

    if (
        pageAudioSnapshot.state === "preparing" ||
        pageAudioSnapshot.state === "playing" ||
        pageAudioSnapshot.state === "paused"
    ) {
        return (
            <PageAudioControl
                state={state}
                pageAudioState={pageAudioSnapshot.state}
                completedSteps={pageAudioSnapshot.completedSteps}
                totalSteps={pageAudioSnapshot.totalSteps}
            />
        );
    }

    return (
        <span>
            <SpeechControlsCrossFade
                transitionKey={speechControlsKey}
                showGradient={!isPlayingOrPaused}
            >
                <Render
                    id="speakQuotesLink_notPlayingOrPaused"
                    when={!isPlayingOrPaused}
                >
                    <PlaybackModePicker
                        state={state}
                        pageAudioAvailable={IsGoogleBookOfMindVoice(
                            GetBookOfMindSavedVoiceName()
                        )}
                        onSelect={(mode) =>
                            StartSelectedPlaybackMode(state, mode)
                        }
                    />
                </Render>
                <Render
                    id="speakQuotesLink_playingOrPaused"
                    when={isPlayingOrPaused}
                >
                    <ActiveSpeechControls {...{ state }} />
                </Render>
            </SpeechControlsCrossFade>
        </span>
    );
}

function StartSelectedPlaybackMode(
    state: IBookOfMindState,
    mode: BookOfMindPlaybackMode
) {
    if (mode === "quote") {
        ToggleSpeech(state);
        return;
    }

    const pageAudioState = GetBookOfMindStateForPageAudio(state);
    const pageAudioDiagnostics = GetBookOfMindPageAudioDiagnostics(pageAudioState);
    const quotes = pageAudioState.remainingQuotesToDisplay
        .slice(0, pageAudioState.numberOfQuotesToDisplay)
        .map((quote) => quote.quote);
    const title =
        pageAudioState.rootNodeName === fromOthersNodeName
            ? "The Book of Mind"
            : pageAudioState.rootNodeName;
    const includeTitle = pageAudioState.rootNodeName !== "Related Quotes";

    LogPageAudioSelection(
        pageAudioState,
        pageAudioDiagnostics,
        "playback-mode-picker"
    );
    void bookOfMindSpeech
        .PlayPageAudioAsync(
            title,
            quotes,
            includeTitle,
            pageAudioDiagnostics
        )
        .catch(
            (error: unknown) => {
                toastr.error(
                    error instanceof Error
                        ? error.message
                        : "Unable to prepare page audio"
                );
            }
        );
}

function PageAudioControl({
    state,
    pageAudioState,
    completedSteps,
    totalSteps,
}: {
    state: IBookOfMindState;
    pageAudioState: "preparing" | "playing" | "paused";
    completedSteps?: number;
    totalSteps?: number;
}) {
    const pageAudioStateForRequest = GetBookOfMindStateForPageAudio(state);
    const pageAudioDiagnostics = GetBookOfMindPageAudioDiagnostics(
        pageAudioStateForRequest
    );
    const quotes = pageAudioStateForRequest.remainingQuotesToDisplay
        .slice(0, pageAudioStateForRequest.numberOfQuotesToDisplay)
        .map((quote) => quote.quote);
    const title =
        pageAudioStateForRequest.rootNodeName === fromOthersNodeName
            ? "The Book of Mind"
            : pageAudioStateForRequest.rootNodeName;
    const includeTitle =
        pageAudioStateForRequest.rootNodeName !== "Related Quotes";
    const isPreparing = pageAudioState === "preparing";
    const isPlaying = pageAudioState === "playing";
    const label = isPlaying ? "Pause page audio" : "Resume page audio";

    if (isPreparing) {
        return (
            <PageAudioPreparingControl
                completedSteps={completedSteps}
                totalSteps={totalSteps}
            />
        );
    }

    return (
        <IconWithText
            iconSuffix={
                isPreparing
                    ? EGlyphicon.hourglass
                    : isPlaying
                      ? EGlyphicon.pause
                      : EGlyphicon.play
            }
            whenClicked={() => {
                if (isPreparing) {
                    return;
                }

                if (isPlaying) {
                    bookOfMindSpeech.PausePageAudio();
                    return;
                }

                LogPageAudioSelection(
                    pageAudioStateForRequest,
                    pageAudioDiagnostics,
                    "page-audio-control"
                );
                void bookOfMindSpeech
                    .PlayPageAudioAsync(
                        title,
                        quotes,
                        includeTitle,
                        pageAudioDiagnostics
                    )
                    .catch((error: unknown) => {
                        toastr.error(
                            error instanceof Error
                                ? error.message
                                : "Unable to prepare page audio"
                        );
                    });
            }}
            displayAsBlock={false}
            withClass={`${styles.controlButton} ${styles.iconOnlyControlButton} ${
                isPreparing || isPlaying ? styles.controlButtonActive : ""
            }`}
            tooltip={label}
            ariaLabel={label}
            useButton={true}
        />
    );
}

function LogPageAudioSelection(
    state: IBookOfMindState,
    pageAudioDiagnostics: IBookOfMindPageAudioDiagnostics,
    source: "playback-mode-picker" | "page-audio-control"
) {
    LogClientDiagnostic("book-of-mind-speech", "page-audio-selection", {
        source,
        rootNodeName: state.rootNodeName,
        searchTextLength: state.searchText.length,
        ...pageAudioDiagnostics,
    });
}

function PageAudioPreparingControl({
    completedSteps,
    totalSteps,
}: {
    completedSteps?: number;
    totalSteps?: number;
}) {
    const hasProgress =
        typeof completedSteps === "number" &&
        typeof totalSteps === "number" &&
        Number.isInteger(completedSteps) &&
        Number.isInteger(totalSteps) &&
        totalSteps > 0 &&
        completedSteps >= 0;
    const normalizedCompletedSteps = hasProgress
        ? Math.min(completedSteps, totalSteps)
        : 0;
    const circumference = 2 * Math.PI * 9;
    const progressLabel = hasProgress
        ? `Preparing page audio: ${normalizedCompletedSteps} of ${totalSteps} steps`
        : "Preparing page audio...";

    return (
        <TouchActionButton
            className={`${styles.controlButton} ${styles.iconOnlyControlButton} ${styles.controlButtonActive}`}
            ariaLabel={progressLabel}
            onActivate={() =>
                Sounds.wooden_tap.Play({
                    allowConcurrent: true,
                    volume: 0.6,
                })
            }
        >
            <span
                className={styles.pageAudioProgressIcon}
                data-tip={progressLabel}
                aria-hidden="true"
            >
                <svg
                    className={styles.pageAudioProgressRing}
                    viewBox="0 0 24 24"
                    focusable="false"
                >
                    <circle
                        className={styles.pageAudioProgressRingTrack}
                        cx="12"
                        cy="12"
                        r="9"
                    />
                    <circle
                        className={`${styles.pageAudioProgressRingIndicator} ${
                            hasProgress
                                ? ""
                                : styles.pageAudioProgressRingIndeterminate
                        }`}
                        cx="12"
                        cy="12"
                        r="9"
                        strokeDasharray={
                            hasProgress
                                ? circumference
                                : `${circumference * 0.28} ${
                                      circumference * 0.72
                                  }`
                        }
                        strokeDashoffset={
                            hasProgress
                                ? circumference *
                                  (1 - normalizedCompletedSteps / totalSteps)
                                : 0
                        }
                    />
                </svg>
                <span className={styles.pageAudioProgressHourglass}>
                    <Glyphicon icon={EGlyphicon.hourglass} />
                </span>
            </span>
        </TouchActionButton>
    );
}
